import { Table } from "@xola/ui-kit";
import { ColDef } from "ag-grid-community";
import { useDebounceEffect } from "ahooks";
import { debounce } from "lodash";
import React from "react";
import { useLocation } from "react-router-dom";
import { ExperiencePurchaseItem } from "types";
import { DashboardTableGroupHeader } from "../DashboardTableGroupHeader";
import { RosterColumnId } from "../Roster/RosterGuestList/helpers";
import {
    columnsToExclude,
    getDynamicColumnWidths,
    getQuestionnaireFields,
    HeaderRow,
    QuestionnaireRow,
    Row,
} from "../Roster/RosterGuestListForPrint";
import { GroupedData } from "./useDashboardTableData";

interface DashboardTablePrintViewProps {
    columnDefs: ColDef[];
    groupedData?: GroupedData[];
}

export const DashboardTablePrintView = ({ columnDefs, groupedData }: DashboardTablePrintViewProps) => {
    const print = useLocation<{ print?: boolean }>().state?.print ?? false;
    const visibleColumns = columnDefs.filter(
        (column) => !column.hide && !columnsToExclude.has(column.colId as RosterColumnId),
    );

    const visibleColumnIds = visibleColumns.map((column) => column.colId as RosterColumnId);
    const dynamicColumnWidths = getDynamicColumnWidths(visibleColumnIds);

    const shouldPrint = print && visibleColumns.length > 0;
    useDebounceEffect(() => {
        if (shouldPrint) {
            debounce(() => {
                // Timeout to allow the component to be completely rendered before opening the print dialog
                setTimeout(() => window.print(), 2000);
            }, 200);
        }
    }, [shouldPrint]);

    let summaryRowExists = false;

    return (
        <div className="no-translate absolute inset-0 hidden print:visible print:block">
            {groupedData?.map((group) => {
                if (!group?.items || !Array.isArray(group.items)) {
                    return null;
                }

                return (
                    <div key={group.groupLabel} className="mt-10">
                        <DashboardTableGroupHeader isPrint purchaseItems={group.items} groupId={group.groupLabel} />
                        {group.rowData.map((row, index) => {
                            if (!("item" in row)) {
                                summaryRowExists = true;
                                return null;
                            }

                            const questionnaires = getQuestionnaireFields(row);
                            return (
                                <div key={row.item.id} className="print-table-container">
                                    <Table style={{ tableLayout: "fixed", width: "100%" }}>
                                        {index === 0 || (index === 1 && summaryRowExists) ? (
                                            <HeaderRow
                                                columnDefs={
                                                    visibleColumns && visibleColumns.length > 0
                                                        ? visibleColumns
                                                        : columnDefs
                                                }
                                                dynamicColumnWidths={dynamicColumnWidths}
                                            />
                                        ) : null}

                                        <Table.Body>
                                            <>
                                                <Row
                                                    item={row.item as ExperiencePurchaseItem}
                                                    guest={row.guest}
                                                    organizer={row.organizer}
                                                    isOrganizer={row.isOrganizer}
                                                    lineItem={row.demographic}
                                                    purchaseNotes={row.purchaseNotes}
                                                    guestNotes={row.guestNotes}
                                                    columnDefsOrder={visibleColumns}
                                                    dynamicColumnWidths={dynamicColumnWidths}
                                                />
                                                {questionnaires.length > 0 && (
                                                    <QuestionnaireRow
                                                        data={questionnaires}
                                                        columns={columnDefs.length}
                                                    />
                                                )}
                                            </>
                                        </Table.Body>
                                    </Table>
                                </div>
                            );
                        })}
                    </div>
                );
            })}
        </div>
    );
};
